<script>
    import Computation from '@js/modules/computation';
    import I18n from '@js/modules/translations';
    import {PaceRideTypes} from '@js/components/modules/constants';

    import Icon from '@js/components/themes/Icon.svelte';
    import visibilityOffIcon from '@js/components/icons/visibilityOffIcon';
    import mapClockIcon from '@js/components/icons/mapClockIcon';
    import heartIcon from '@js/components/icons/heartIcon';

    import FavoriteButton from '@js/components/ride/FavoriteButton.svelte';

    import {RIDE_TYPE_ICON} from '@js/components/themes/icons';

    const {
        ride,
        // A list worth knowing which card was opened (AnalyticsService.trackTaggedClicks): its name,
        // the card's rank, the activity tab.
        trackList,
        trackPosition,
        trackActivity
    } = $props();
</script>

<a class="relative group block my-4 mx-0 md:mx-2 lg:px-2"
   href={ride.link}
   data-track={trackList ? 'ride_card_click' : undefined}
   data-track-list={trackList}
   data-track-position={trackList ? trackPosition : undefined}
   data-track-activity={trackActivity}>
    <div class="relative overflow-hidden rounded-xl shadow-md transition duration-500 ease-in-out transform group-hover:scale-[1.02] group-hover:shadow-2xl bg-white">
        <div class="relative overflow-hidden">
            {#if ride.defaultPicture}
                <img class="w-full h-48 md:h-56 object-cover object-center transition duration-500 ease-in-out transform group-hover:scale-110"
                     srcset={ride.defaultPicture.medium ? ride.defaultPicture.medium.jpg : ride.defaultPicture.jpg}
                     src={ride.defaultPicture.medium ? ride.defaultPicture.medium.jpg : ride.defaultPicture.jpg}
                     alt={ride.name}/>
            {/if}
            <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-black/5 to-transparent"></div>

            <div class="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
                <div class="flex items-start flex-wrap flex-col gap-2">
                    <div class="inline-flex items-center px-3 py-1.5 text-white text-xs font-semibold bg-primary-600/90 backdrop-blur-sm rounded-full shadow-lg"
                         title={ride.rideTypeTranslated}>
                        <Icon class="inline-flex h-4 w-4 me-1.5"
                              icon={RIDE_TYPE_ICON[ride.rideType]}/>
                        {ride.rideTypeTranslated}
                    </div>

                    <div>
                        {#if ride.planned}
                            <div class="inline-flex items-center justify-center w-8 h-8 mr-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg"
                                 title={I18n.t('js.ride.common.planned')}>
                                <Icon class="w-4 h-4 text-primary-800"
                                      icon={mapClockIcon}/>
                            </div>
                        {/if}

                        {#if ride.visibility === 'only_me'}
                            <div class="inline-flex items-center justify-center w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg"
                                 title={I18n.t('js.ride.enums.visibility.only_me')}>
                                <Icon class="w-4 h-4 text-primary-800"
                                      icon={visibilityOffIcon}/>
                            </div>
                        {/if}
                    </div>
                </div>

                <div class="flex flex-col items-end gap-2">
                    {#if ride.level !== 'level_undefined'}
                        <div class="px-3 py-1.5 text-white text-xs font-bold bg-primary-600/90 backdrop-blur-sm border border-white/30 rounded-full shadow-lg"
                             title={ride.levelTranslated}>
                            {ride.levelTranslated}
                        </div>
                    {/if}

                    <FavoriteButton rideId={ride.id}/>
                </div>
            </div>

            {#if ride.bookmarksCount > 0}
                <div class="absolute bottom-3 left-3"
                     title={I18n.t('js.ride.favorites.social_proof', {count: ride.bookmarksCount})}>
                    <div class="inline-flex items-center px-2.5 py-1.5 text-white/90 text-xs font-normal bg-black/40 backdrop-blur-sm rounded-full shadow-md">
                        <Icon class="w-3.5 h-3.5 mr-1.5 text-red-400" icon={heartIcon}/>
                        {ride.bookmarksCount}
                    </div>
                </div>
            {/if}

            {#if ride.user}
                <div class="absolute bottom-3 right-3">
                    <div class="inline-flex items-center px-2.5 py-1.5 text-white/90 text-xs font-normal bg-black/40 backdrop-blur-sm rounded-full shadow-md">
                        <svg class="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                  clip-rule="evenodd"/>
                        </svg>
                        <span class="truncate max-w-[120px]">{ride.user.pseudo}</span>
                    </div>
                </div>
            {/if}
        </div>

        <div class="p-4">
            <h3 class="h-[58px] text-lg md:text-xl font-bold leading-tight text-gray-900 line-clamp-2 mb-3 group-hover:text-primary-700 transition-colors">
                {ride.name}
            </h3>

            <div class="flex items-center justify-start gap-x-2 gap-y-2 text-sm font-medium text-gray-700 mb-3">
                <div class="flex items-center">
                    <span class="text-base font-bold text-gray-900">
                        {@html Computation.formatDistance(ride.distance, {
                            smartUnit: true,
                            kUnit: false,
                            decimalLimit: 2
                        }).replace(/([^.,\d]+)/g, '<span class="text-xs text-gray-500 font-normal ml-0.5">$1</span>')}
                    </span>
                </div>

                {#if !!ride.formattedDuration}
                    <div class="flex items-center">
                        <span class="text-gray-400 mx-0.5">•</span>
                    </div>
                    <div class="flex items-center">
                        <span class="text-base font-bold text-gray-900">
                            {@html ride.formattedDuration.replace(/([^.,\d]+)/g, '<span class="text-xs text-gray-500 font-normal ml-0.5">$1</span>')}
                        </span>
                    </div>
                {/if}

                {#if PaceRideTypes.includes(ride.rideType) && !!ride.speed}
                    <div class="flex items-center">
                        <span class="text-gray-400 mx-0.5">•</span>
                    </div>
                    <div class="flex items-center">
                        <span class="text-base font-bold text-gray-900">
                            {@html (Computation.formatPace(Computation.speedToUnit(ride.speed, {})) || '-').replace(/([^.,\d:]+)/g, '<span class="text-xs text-gray-500 font-normal ml-0.5">$1</span>')}
                        </span>
                    </div>
                {:else if ride.rideType === 'bike' && !!ride.speed}
                    <div class="flex items-center">
                        <span class="text-gray-400 mx-0.5">•</span>
                    </div>
                    <div class="flex items-center">
                        <span class="text-base font-bold text-gray-900">
                            {@html Computation.formatSpeed(ride.speed).replace(/([^.,\d]+)/g, '<span class="text-xs text-gray-500 font-normal ml-0.5">$1</span>')}
                        </span>
                    </div>
                {/if}

                {#if (ride.rideType === 'hiking' && !!ride.positiveElevation)}
                    <div class="flex items-center">
                        <span class="text-gray-400 mx-0.5">•</span>
                    </div>
                    <div class="flex items-center">
                        <span class="text-base font-bold text-gray-900">
                            +{@html Computation.formatDistance(ride.positiveElevation, {
                            smartUnit: false,
                            kUnit: false,
                            decimalLimit: 2
                        }).replace(/([^.,\d]+)/g, '<span class="text-xs text-gray-500 font-normal ml-0.5">$1</span>')}
                        </span>
                    </div>
                {/if}
            </div>

            {#if ride.placesBreadcrumb?.length}
                <div class="flex items-center text-sm text-gray-500 border-t border-gray-100 pt-3">
                    <svg class="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <span class="truncate">
                    {ride.placesBreadcrumb.map((place) => place.title)
                        .slice(1, 3)
                        .join(' • ')}
                </span>
                </div>
            {/if}
        </div>
    </div>
</a>
